import { AxiosError } from "axios";
import { ApiError } from "../types/apiError";

export class BusinessError extends Error {
  isBusinessError = true;

  constructor(message: string) {
    super(message);
    this.name = "BusinessError";
  }
}

export const normalizeApiError = (error: unknown): ApiError => {
  // 1️⃣ Business error
  if (error instanceof BusinessError) {
    return {
      message: error.message,
      isBusinessError: true,
      isNetworkError: false,
    };
  }

  // 2️⃣ Axios error
  if (error instanceof AxiosError) {
    const isNetworkError =
      !error.response ||
      error.code === "ECONNABORTED" ||
      error.message?.toLowerCase().includes("timeout");

    // Backend often returns HTTP 200 with {"response": {"status": false, "message": "..."}}
    // So we check the nested `response.message` first before falling back to the top-level message.
    const backendMessage =
      error.response?.data?.response?.message ||
      error.response?.data?.message ||
      error.message ||
      "Network error occurred";

    const rawErrors =
      error.response?.data?.errors ||
      error.response?.data?.response?.errors ||
      error.response?.data?.error ||
      error.response?.data?.response?.error;

    let normalizedErrors: { field: string; message: string }[] | undefined;
    if (Array.isArray(rawErrors)) {
      normalizedErrors = rawErrors;
    } else if (rawErrors && typeof rawErrors === 'object') {
      normalizedErrors = Object.entries(rawErrors).map(([field, msg]) => ({
        field,
        message: typeof msg === 'string' ? msg : JSON.stringify(msg),
      }));
    }

    return {
      message: backendMessage,
      status: error.response?.status,
      code: error.code,
      errorCode: error.response?.data?.code || error.response?.data?.response?.code,
      isNetworkError,
      isBusinessError: !isNetworkError,
      errors: normalizedErrors,
      responseData: error.response?.data,
    };
  }

  // 3️⃣ Fallback for objects with message property (e.g. plain JSON errors)
  if (error && typeof error === 'object' && 'message' in error) {
    return {
      message: (error as any).message || "Something went wrong",
      isNetworkError: false,
      isBusinessError: true,
    };
  }

  // 4️⃣ Generic Fallback
  return {
    message: "Something went wrong",
    isNetworkError: false,
  };
};